import React, {FC, ReactElement, useMemo} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid} from "@mui/material";
import {formatDate} from "../../../utils/DataFormat";
import {customersStore} from "../../../store/CustomersStore";
import {GetSalesInvoiceDetailsDto} from "../../../model/invoice/sales/GetSalesInvoiceDetailsDto";
import {selectedCompanyStore} from "../../../store/SelectedCompany";
import {Email} from "@mui/icons-material";
import LoadingButton from "../../../components/button/LoadingButton";
import CancelIcon from "@mui/icons-material/Cancel";
import {Formik} from "formik";
import {FormikProps} from "formik/dist/types";
import TextFieldWithValidation from "../../../components/input/TextFieldWithValidation";
import * as Yup from 'yup';
import {Nil} from "../../../utils/Types";
import {salesInvoiceApi} from "../../../backend/SalesInvoiceApi";
import {userInfoStore} from "../../../store/UserInfo";
import {TFunction, Trans, useTranslation} from "react-i18next";
import {getFixedTranslation} from "../../../locale/i18";

interface IProps {
    invoice: GetSalesInvoiceDetailsDto,
    open: boolean,
    onSend: () => void,
    onClose: () => void
}

interface SendEmailView {
    sendTo: string,
    cc?: string,
    subject: string,
    body: string,
    invoiceFileName: string
}

const splitEmails = (emailsComaSeparated: string | Nil) => {
    if (emailsComaSeparated) {
        return emailsComaSeparated.split(",").map(it => it.trim());
    } else {
        return [];
    }
}

const newSendEmailViewSchema = (t: TFunction) => Yup.object().shape({
    sendTo: Yup.string()
            .trim()
            .email(t("common.error.invalid_email"))
            .required(t("common.error.required_field")),
    subject: Yup.string()
            .trim()
            .required(t("common.error.required_field")),
    body: Yup.string()
            .trim()
            .required(t("common.error.required_field")),
    invoiceFileName: Yup.string()
            .trim()
            .required(t("common.error.required_field")),
    cc: Yup.string()
            .trim()
            .test((function (value) {
                if (value) {
                    const notValidEmail = splitEmails(value).find(it => !Yup.string().email().isValidSync(it));
                    if (notValidEmail) {
                        return this.createError({message: t("common.error.invalid_email_with_address", {email: notValidEmail})});
                    }
                }
                return true;
            })),
});

const RenderDialogContent = ({formik, open, onClose}: { formik: FormikProps<SendEmailView>, open: boolean, onClose: () => void }) => {
    return (
            <>
                <Dialog open={open} onClose={onClose} fullWidth={true} scroll="paper">
                    <DialogTitle><Trans i18nKey="send_invoice_via_email.title">Send invoice</Trans></DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid xs={12} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="sendTo" label={<Trans i18nKey="send_invoice_via_email.to">Send invoice to (email address)</Trans>}
                                        defaultValue={formik.values.sendTo}
                                        fullWidth
                                        onChange={formik.handleChange}
                                        hasError={formik.errors.sendTo}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="subject" label={<Trans i18nKey="send_invoice_via_email.subject">Email subject</Trans>}
                                        defaultValue={formik.values.subject}
                                        fullWidth
                                        onChange={formik.handleChange}
                                        hasError={formik.errors.subject}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="body" label={<Trans i18nKey="send_invoice_via_email.body">Email body</Trans>}
                                        defaultValue={formik.values.body}
                                        multiline
                                        rows={6}
                                        maxRows={8}
                                        fullWidth
                                        onChange={formik.handleChange}
                                        hasError={formik.errors.body}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="invoiceFileName" label={<Trans i18nKey="send_invoice_via_email.file_name">Attached invoice file name</Trans>}
                                        defaultValue={formik.values.invoiceFileName}
                                        fullWidth
                                        onChange={formik.handleChange}
                                        hasError={formik.errors.invoiceFileName}
                                />
                            </Grid>
                            <Grid xs={12} item>
                                <TextFieldWithValidation
                                        autoComplete="off"
                                        name="cc"
                                        label={<Trans i18nKey="send_invoice_via_email.copy_to">Send copy to (emails comma-separated)</Trans>}
                                        defaultValue={formik.values.cc}
                                        fullWidth
                                        onChange={formik.handleChange}
                                        hasError={formik.errors.cc}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button color="secondary" variant={"outlined"} onClick={onClose} startIcon={<CancelIcon/>}>
                            <Trans i18nKey="common.close">Close</Trans>
                        </Button>
                        <LoadingButton onClick={formik.submitForm} variant={"outlined"} startIcon={<Email/>} color="primary">
                            <Trans i18nKey="common.send">Send</Trans>
                        </LoadingButton>
                    </DialogActions>
                </Dialog>
            </>
    );
}


export const SendSalesInvoiceDialog: FC<IProps> = (dialogProps): ReactElement => {
    const sendInvoiceViaEmail = async (changed: SendEmailView) => {
        await salesInvoiceApi.sendInvoice(dialogProps.invoice.id, {
            sendTo: changed.sendTo,
            subject: changed.subject,
            body: changed.body,
            invoiceFileName: changed.invoiceFileName,
            cc: splitEmails(changed.cc)
        });
        dialogProps.onSend();
    }

    const invoice = dialogProps.invoice;

    const initState = useMemo(() => {
        const customer = customersStore.find(invoice.customerId)!!;
        const myCompany = userInfoStore.findById(selectedCompanyStore.companyId)!!;
        const customerInvoiceLangTranslation = getFixedTranslation(customer.invoiceLang);

        return {
            sendTo: customer.email ?? "",
            subject: customerInvoiceLangTranslation("send_invoice_via_email.email_header", {invoiceNo: invoice.invoiceNo, companyName: myCompany.name}),
            body: customerInvoiceLangTranslation("send_invoice_via_email.email_body", {invoiceNo: invoice.invoiceNo, companyName: myCompany.name}),
            invoiceFileName: invoice.invoiceNo + " " + myCompany.name + " " + formatDate(invoice.documentDate) + ".pdf",
        }
    }, [invoice]);

    const {t} = useTranslation();
    const sendEmailViewSchema = useMemo(() => {
        return newSendEmailViewSchema(t)
    }, [t])

    return <Formik initialValues={initState} onSubmit={sendInvoiceViaEmail} validationSchema={sendEmailViewSchema}>
        {(props: FormikProps<SendEmailView>) => <RenderDialogContent formik={props} onClose={dialogProps.onClose} open={dialogProps.open}/>}
    </Formik>
}

export default SendSalesInvoiceDialog;